import {
    createChatRequest,
    getIndexOfFirstUnansweredChat,
    shouldSaveToServer,
    willSelectBodyRegionAutomatically
} from "../ChatUtils";
import {
    getPreviousEditableChatInd,
    onSaveSingleClick,
    removeChildQuestions,
    toggleCheckboxChatResponse
} from "../ChatServices";
import {
    clearChatBotProperty, clearChatbotReduxStore, setAddDependsOnQuestionIds, setBodyRegionList,
    setBotProgressbarValue, setChatBotErrorMsg, setChats,
    setCurrentChatIndex, setIsFetchingChatQuestion, setOpenChatBot, setPrevChatIndex,
    setSessionId, setShowChatBot, updateChatAtIndex
} from "../../../../features/chat/chatbotSlice";
import { useDispatch, useSelector } from "react-redux";
import GLOBAL from "../../../../global";
import CONSTANT from "../../../../constants/Constant";
import ChatRequestDetail from "../../../../models/ChatRequestDetail";
import { saveOrCreateNewAssessment } from "../business/chatbotService";
import chatbotService from "../../../../services/ChatbotService";
import Chat from "../../../../models/Chat";
import { useContext } from "react";
import AppContext from "../../../../contexts/AppContext";
import { deepClone, playAudio } from "../../../../utils/Utils";
import { _getBodyRegion } from "../business/NewScreeningUtils";
import { useCallbackContext } from "../../../../contexts/CallbackContext";
import { resetEventData, setTestIdOfAskEmma } from "../../../../features/askEmma/askEmmaSlice";


const useChatbot = () => {
    const { isAudioPlayEnabled, audioPlayEnabled } = useContext(AppContext); //TODO: will replace with redux

    const dispatch = useDispatch();
    const botState = useSelector(state => state.chatBot);
    const { registerCallback, unregisterCallback, getCallback } = useCallbackContext();

    const botData = botState?.botData || {};

    const chats = botData?.chats || [];
    const currentChatIndex = botData?.currentChatIndex ?? -1;
    const selectedBotInfoObj = botData?.botInfo || {};
    const sessionId = botData?.sessionId || '';
    const invitationId = botData?.invitationId || '';
    let invitationTypeName = (botData?.invitationTypeName || '').toLowerCase();
    const isFollowup = botData?.isFollowup || false;
    const dependsOnQuestionIds = botData?.dependsOnQuestionIds || [];
    const callBackFnKeyAfterCloseBot = botData?.callBackFnKeyAfterCloseBot || "";

    const patientId = (GLOBAL.USER_INFO && GLOBAL.USER_INFO['ContactId']) || '';

    const startChatBot = (
                                        invitationData,
                                        patientId,
                                        meetingStart,
                                        meetingEnd,
                                        callBackFnAfterCloseBot
                                    ) => {
        // Ensure mandatory properties are present
        if (!invitationData || !patientId) {
            console.error("invitationData and patientId are mandatory");
            return;
        }

        let tempCallBackFnKeyAfterCloseBot = `callbackFnAfterCloseBot_${Date.now()}`

        if(callBackFnAfterCloseBot) {
            registerCallback(tempCallBackFnKeyAfterCloseBot, callBackFnAfterCloseBot);
        }

        // Dispatch the action with the provided or derived parameters
        dispatch(setOpenChatBot({
            invitationData,
            patientId,
            meetingStart: meetingStart || invitationData?.MeetingStart || invitationData?.MeetingEndDate,
            meetingEnd: meetingEnd || invitationData?.MeetingEnd || invitationData?.MeetingStartDate,
            callBackFnKeyAfterCloseBot: tempCallBackFnKeyAfterCloseBot,
        }));
    };

    const prepareChatbotAnswer = async (chat, removeChildQuestionIds = []) => {
        let { mode } = chat;
        if (chat?.answers && (chat?.answers.length === 0)) {
            return;
        }

        ////TODO: Have to remove those
        GLOBAL.SAVED_ANSWERS = GLOBAL.SAVED_ANSWERS.filter((answer) => answer.QuestionId !== chat.questionId);

        const { questionId, intent } = chat;
        let tempChats = deepClone(chats);
        const index = tempChats.findIndex(item => item.questionId === questionId);

        if (index > -1) {
            let tempChat = deepClone(tempChats[index]);
            tempChat = { ...tempChat, ...chat };
            tempChat.answered = true;

            tempChats[index] = tempChat;
            dispatch(updateChatAtIndex({ index, chat: tempChat }));
        }

        // noinspection JSIgnoredPromiseFromCall
        await fetchAndProcessChatQuestion(intent, mode, tempChats, index, removeChildQuestionIds);
    }

    const setCurrentAndPrevChatIndex = (chats) => {
        if (!chats || chats.length === 0) {
            dispatch(setCurrentChatIndex(-1));
            dispatch(setPrevChatIndex(-1));
            return -1;
        }

        let firstUnansweredChatInd = getIndexOfFirstUnansweredChat(chats);

        // If no unanswered chat found, set to the last chat index
        if (firstUnansweredChatInd === -1) {
            firstUnansweredChatInd = chats.length - 1;
        }

        // Calculate previous editable chat index
        const tempPrevChatIndex = getPreviousEditableChatInd(chats, firstUnansweredChatInd - 1);

        // Dispatch in the correct order to ensure synchronization
        dispatch(setCurrentChatIndex(firstUnansweredChatInd));
        dispatch(setPrevChatIndex(tempPrevChatIndex));

        return firstUnansweredChatInd;
    }

    const calculateChatbotProgress = (botName = '') => {
        let totalCount = 4;
        if (botName === CONSTANT.CHAT_BOTS.INTAKE_BOT) {
            totalCount = 3;
        }

        let countStep = GLOBAL.ASSESSMENT?.ADLCompleted ? 1 : 0;
        countStep += GLOBAL.ASSESSMENT?.MedicalHistoryCompleted ? 1 : 0;
        countStep += GLOBAL.ASSESSMENT?.ChiefComplaintsCompleted ? 1 : 0;
        countStep += GLOBAL.ASSESSMENT?.BiometricCompleted ? 1 : 0;

        let percentage = 100 / totalCount;
        dispatch(setBotProgressbarValue(Math.round(percentage * countStep)));
    }

    const insertNewChatQuestionIntoState = (chat, updatedChats) => {
        const childQuestionIdsList = GLOBAL?.parentAndChildQuestionIds?.childQuestionIds || [];
        const parentQuestionId = GLOBAL?.parentAndChildQuestionIds?.parentQuestionId || -1;

        console.log('🔧 insertNewChatQuestionIntoState - GLOBAL check:', {
            hasParentChildData: Object.keys(GLOBAL?.parentAndChildQuestionIds).length > 0,
            parentQuestionId,
            childQuestionIdsList,
            currentQuestionId: chat?.questionId,
            globalObjectKeys: Object.keys(GLOBAL?.parentAndChildQuestionIds || {}),
            entireGlobalObject: GLOBAL?.parentAndChildQuestionIds
        });

        // Check if this question should be treated as a child question
        // Only treat as child if there's parent-child data AND this question is in the expected list
        const shouldTreatAsChildQuestion = Object.keys(GLOBAL?.parentAndChildQuestionIds).length > 0 &&
                                         childQuestionIdsList.includes(chat?.questionId);

        if (!shouldTreatAsChildQuestion) {
            // Check if this question already exists in the chat array
            const existingQuestionIndex = updatedChats.findIndex(existingChat => existingChat.questionId === chat.questionId);

            if (existingQuestionIndex === -1) {
                // Question doesn't exist, add it
                updatedChats = [...updatedChats, chat];
                dispatch(setChats(updatedChats));
            } else {
                // Question already exists, just update the existing one instead of adding duplicate
                console.log('🔄 Question already exists, updating existing chat instead of adding duplicate:', {
                    questionId: chat.questionId,
                    existingIndex: existingQuestionIndex
                });
                updatedChats[existingQuestionIndex] = chat;
                dispatch(setChats(updatedChats));
            }

            // Calculate and dispatch the indices in the correct order to ensure synchronization
            const firstUnansweredChatInd = getIndexOfFirstUnansweredChat(updatedChats);
            const finalFirstUnansweredChatInd = firstUnansweredChatInd === -1 ? updatedChats.length - 1 : firstUnansweredChatInd;
            const tempPrevChatIndex = getPreviousEditableChatInd(updatedChats, finalFirstUnansweredChatInd - 1);

            console.log('🔍 insertNewChatQuestionIntoState - Regular question, setting indices:', {
                totalChats: updatedChats.length,
                currentChatIndex: finalFirstUnansweredChatInd,
                prevChatIndex: tempPrevChatIndex,
                prevQuestionId: tempPrevChatIndex >= 0 ? updatedChats[tempPrevChatIndex]?.questionId : 'N/A',
                newChatQuestionId: chat?.questionId,
                wasInChildList: childQuestionIdsList.includes(chat?.questionId),
                childQuestionIdsList: childQuestionIdsList
            });

            dispatch(setCurrentChatIndex(finalFirstUnansweredChatInd));
            dispatch(setPrevChatIndex(tempPrevChatIndex));
            return;
        }

        insertChildChatQuestionAtCorrectPosition(chat, parentQuestionId, childQuestionIdsList, updatedChats);
    };

    const insertChildChatQuestionAtCorrectPosition = (chat, parentQuestionId, childQuestionIdsList, updatedChats)=> {
        let lastPresentChildQuestionIndex = -1;
        let totalChildQuestionFetched = 0;
        let insertionIndex = -1;

        // Create tempChats first to search in the current state
        const tempChats = [...updatedChats];

        for(let i = 0; i < childQuestionIdsList.length; i++) {
            for(let j = 0; j < tempChats.length; j++) {
                if(tempChats[j].questionId === childQuestionIdsList[i]) {
                    lastPresentChildQuestionIndex = j;
                    totalChildQuestionFetched = i + 1;
                    break;
                }
            }
        }

        const isCurrentQuestionAlreadyPresent = tempChats.findIndex(tempChat=> tempChat.questionId === chat.questionId);

        if(isCurrentQuestionAlreadyPresent === -1) {
            if(lastPresentChildQuestionIndex === -1) {
                const parentQuestionIndex = tempChats.findIndex(tempChat => tempChat.questionId === parentQuestionId);
                insertionIndex = parentQuestionIndex + 1;
                tempChats.splice(insertionIndex, 0, chat);
            }
            else {
                insertionIndex = lastPresentChildQuestionIndex + 1;
                tempChats.splice(insertionIndex, 0, chat);
            }
            dispatch(setChats(tempChats));
        }

        // For child questions: currentChatIndex should advance to the last inserted position
        // to maintain proper chat flow progression, but only when a new question is actually inserted
        let finalCurrentChatIndex;

        console.log('🐛 DEBUG - Child insertion logic:', {
            isCurrentQuestionAlreadyPresent,
            insertionIndex,
            questionId: chat?.questionId,
            tempChatsLength: tempChats.length,
            lastPresentChildQuestionIndex,
            parentQuestionId,
            childQuestionIdsList: childQuestionIdsList,
            currentQuestionInList: childQuestionIdsList.includes(chat?.questionId)
        });

        if (isCurrentQuestionAlreadyPresent === -1) {
            // New question inserted - ALWAYS advance currentChatIndex to the insertion point for child questions
            // This is the core fix: child questions should maintain sequential progression
            finalCurrentChatIndex = insertionIndex;
            console.log('🟢 Using insertionIndex:', insertionIndex);
        } else {
            // Question already exists - this should rarely happen for child questions
            // Don't advance the index in this case
            const firstUnansweredChatInd = getIndexOfFirstUnansweredChat(tempChats);
            finalCurrentChatIndex = firstUnansweredChatInd === -1 ? tempChats.length - 1 : firstUnansweredChatInd;
            console.log('🔴 Question exists, using firstUnanswered:', firstUnansweredChatInd);
        }

        const tempPrevChatIndex = getPreviousEditableChatInd(tempChats, finalCurrentChatIndex - 1);

        console.log('🔍 insertChildChatQuestionAtCorrectPosition - Setting indices:', {
            totalChats: tempChats.length,
            currentChatIndex: finalCurrentChatIndex,
            prevChatIndex: tempPrevChatIndex,
            prevQuestionId: tempPrevChatIndex >= 0 ? tempChats[tempPrevChatIndex]?.questionId : 'N/A',
            newChatQuestionId: chat?.questionId,
            insertionIndex: insertionIndex,
            wasQuestionAlreadyPresent: isCurrentQuestionAlreadyPresent !== -1,
            parentQuestionId: parentQuestionId,
            lastPresentChildIndex: lastPresentChildQuestionIndex,
            totalChildrenFetched: totalChildQuestionFetched,
            childQuestionIdsList: childQuestionIdsList
        });

        dispatch(setCurrentChatIndex(finalCurrentChatIndex));
        dispatch(setPrevChatIndex(tempPrevChatIndex));

        // Clear parent-child relationship when all expected child questions have been processed
        if(totalChildQuestionFetched === childQuestionIdsList.length) {
            console.log('🧹 Clearing parentAndChildQuestionIds - all expected questions fetched:', {
                totalChildQuestionFetched,
                childQuestionIdsListLength: childQuestionIdsList.length,
                childQuestionIdsList
            });
            GLOBAL.parentAndChildQuestionIds = {};
        }
    }

    //updatedCurrentIndex for while automatically selecting body region
    const fetchAndProcessChatQuestion = async (intent, isEditing = false, updatedChats = [], updatedCurrentIndex = -3, removeChildQuestionIds = []) => {//saving and fetching chat
        const { options } = selectedBotInfoObj;
        let chatRequest = createChatRequest(updatedCurrentIndex, updatedChats, intent, isEditing, options, patientId, invitationId, invitationTypeName, isFollowup, sessionId);
        if(intent === CONSTANT.CHATBOT_INTENT.INITIAL) {
            chatRequest.answers = [{
                ...new ChatRequestDetail({ name : "hello" })
            }]
        }

        dispatch(setIsFetchingChatQuestion(true));
        let currentChat = updatedChats[updatedCurrentIndex] || {};

        if (shouldSaveToServer(intent, currentChat)) {
            dispatch(setChatBotErrorMsg(''));

            const response = await saveOrCreateNewAssessment(updatedChats, invitationId); ////TODO: have to compare with onSaveSingleClick
            const { data, success } = response;

            if (!success) {
                if(currentChat) currentChat.answered = false;
                dispatch(setIsFetchingChatQuestion(false));
                setChatBotErrorMsg('Internal server error. Please try again.');
                return;
            }
            dispatch(setTestIdOfAskEmma({ testId: data?.data?.TestId || '' }));
        }

        chatRequest.testId = (GLOBAL.ASSESSMENT && GLOBAL.ASSESSMENT['TestId']) || '';
        chatRequest.demographicsCompleted = (GLOBAL.ASSESSMENT && GLOBAL.ASSESSMENT['DemographicsCompleted']) || false;
        chatRequest.chatEnded = updatedChats[updatedCurrentIndex]?.chatEnded;
        await onSaveSingleClick({ ...chatRequest , responseType: currentChat?.responseType || ''});

        if (currentChat?.lastQuestionInGroup && !isEditing) {
            calculateChatbotProgress(currentChat?.botName);
        }

        if(intent === CONSTANT.CHATBOT_INTENT.BODY_REGION) {
            dispatch(setBodyRegionList(_getBodyRegion(updatedChats)));
        }

        const response = await chatbotService.chat(chatRequest, options.apiUrl);
        const { data, success, error } = response;

        if(removeChildQuestionIds.length > 0) {
            const result = await removeChildQuestions(updatedChats, removeChildQuestionIds, isEditing, dispatch);
            updatedChats = result.chats;
            isEditing = result.mode;
        }

        handleAddingDependsOn(data);
        updatedChats = handleDependsOnQuestionIds(currentChat, response, updatedChats);

        let doesNeedFetchNewQuestion = await handleEditingAnswer(isEditing, updatedChats);
        if(!doesNeedFetchNewQuestion) return;
        dispatch(setIsFetchingChatQuestion(false));

        if (!success) {
            if(currentChat) currentChat.answered = false;
            if(error) {
                setChatBotErrorMsg(error);
            } else {
                setChatBotErrorMsg('Internal server error. Please try again.');
            }
            return;
        }


        const chat = new Chat(data);
        dispatch(setSessionId(chat?.sessionId));
        insertNewChatQuestionIntoState(chat, updatedChats);
    };

    const handleAddingDependsOn = (chat) => {
        const parentQuestionId = chat?.dependsOnQuestionId || 0;
        const childQuestionId = chat?.questionId || 0;

        if(parentQuestionId === 0 || childQuestionId === 0) return;

        dispatch(setAddDependsOnQuestionIds({ parentQuestionId, childQuestionId }));
    }

    // to change responses of child questions when parent question is answered
    const handleDependsOnQuestionIds = (chat, fetchedChat, updatedChats) => {
        const currentChatQuestionId = chat?.questionId || -1;
        const dependsQuestionObj = dependsOnQuestionIds.find(item => item.parentQuestionId === currentChatQuestionId) || {};

        if(Object.keys(dependsQuestionObj).length === 0) return updatedChats;

        const childQuestionIds = dependsQuestionObj.childQuestionId || 0;
        updatedChats = updatedChats.map(item => {
            if(item.questionId === childQuestionIds && (fetchedChat.data?.questionId === childQuestionIds)) {
                item = new Chat({...fetchedChat.data, mode: true });
            }
            return item;
        })
        dispatch(setChats(updatedChats));
        return updatedChats;
    }

    const handleEditingAnswer = async (isEditing, updatedChats) => {
        // Don't skip edit handling when parent-child data exists - this was the main bug!
        // Only skip if not editing
        if (!isEditing) return true;

        dispatch(setIsFetchingChatQuestion(false));
        dispatch(setChats(updatedChats));

        let firstUnansweredChatInd = getIndexOfFirstUnansweredChat(updatedChats);

        console.log('🎯 handleEditingAnswer - Post-edit resume logic:', {
            isEditing,
            hasParentChildData: Object.keys(GLOBAL?.parentAndChildQuestionIds).length > 0,
            totalChats: updatedChats.length,
            answeredChats: updatedChats.filter(chat => chat.answered).length,
            firstUnansweredChatInd,
            firstUnansweredQuestionId: firstUnansweredChatInd >= 0 ? updatedChats[firstUnansweredChatInd]?.questionId : 'none',
            shouldResumeFromUnanswered: firstUnansweredChatInd !== -1,
            action: firstUnansweredChatInd !== -1 ? 'Resuming from next unanswered question' : 'All questions answered - staying at end'
        });

        if (firstUnansweredChatInd === -1) {
            // All questions answered, stay at current position
            console.log('✅ All questions answered - maintaining current position');
            return updatedChats.length > 0;
        }

        // Resume from the next unanswered question - this is the key fix!
        console.log('🚀 Resuming from next unanswered question at index:', firstUnansweredChatInd);
        setCurrentAndPrevChatIndex(updatedChats);
        return false;
    }

    const onChangeUpdateChatsState = async (chat) => {
        const tempChats = deepClone(chats);
        const indexC = tempChats.findIndex(item => item.id === chat.id);
        if (indexC > -1) {
            tempChats[indexC] = chat;
            dispatch(updateChatAtIndex({ index: indexC, chat }));
        }
    };

    const clearAndCloseChatbot = () => {
        dispatch(resetEventData());
        if(callBackFnKeyAfterCloseBot) {
            const callBackFn = getCallback(callBackFnKeyAfterCloseBot);
            if(callBackFn) {
                callBackFn();
                unregisterCallback(callBackFnKeyAfterCloseBot);
            }
        }

        if(isAudioPlayEnabled) {
            onChangeAudioPlay && onChangeAudioPlay();
        }

        // Hide the modal first
        dispatch(setShowChatBot(false));
        // Then clear all the chatbot state (which sets initChatBot: false, unmounting the component)
        dispatch(clearChatBotProperty());
        // dispatch(clearChatbotReduxStore());
        GLOBAL.ASSESSMENT = null;
        GLOBAL.OUTCOME_ASSESSMENT = [];
        GLOBAL.SAVED_ANSWERS = [];
        GLOBAL.CHIEF_COMPLAINTS_ANSWERS = [];
        GLOBAL.ISANYCHIEFCOMPLAINTANSWEDED = false;
        GLOBAL.BIOMETRIC_PROGRESS = 0;
        playAudio("remove");
    };

    const automaticallySelectBodyRegion = async (bodyRegionList, callback) => {
        if (currentChatIndex !== 0) {
            return;
        }

        const chat = chats[currentChatIndex];
        let deepCopyChat = deepClone(chat);
        if (willSelectBodyRegionAutomatically(deepCopyChat, bodyRegionList)) {
            const updatedChat = await selectBodyRegions(deepCopyChat, bodyRegionList);
            await prepareChatbotAnswer(updatedChat);
        }
        if(callback) callback();
    };

    const selectBodyRegions = async (chat, bodyRegionList) => {
        let updatedChat = deepClone(chat);
        for (const chatResponse of chat.responses) {
            if (bodyRegionList.includes(chatResponse.id)) {
                updatedChat = toggleCheckboxChatResponse(updatedChat, chatResponse);
                await onChangeUpdateChatsState(updatedChat);
            }
        }
        return updatedChat;
    };

    const onChangeAudioPlay = async () => {
        await playAudio('remove');
        let wasAudioPlayEnabled = isAudioPlayEnabled;
        GLOBAL.AUDIO_PLAY_ENABLED = !isAudioPlayEnabled;
        if(audioPlayEnabled) audioPlayEnabled(!isAudioPlayEnabled);
        if(!wasAudioPlayEnabled && chats.length > 0 && chats[currentChatIndex]?.audioUrl) {
            await playAudio(chats[currentChatIndex].audioUrl);
        }
    };



    return {
        setCurrentAndPrevChatIndex,
        prepareChatbotAnswer,
        onChangeAudioPlay,
        onChangeUpdateChatsState,
        fetchAndProcessChatQuestion,
        removeChildQuestions,
        calculateChatbotProgress,
        clearAndCloseChatbot,
        automaticallySelectBodyRegion,
        startChatBot
    }
}

export default useChatbot;
